import { headingGuidanceForAges } from "./heading-guidance";

export type AgeingBucket = "current" | "1_30" | "31_60" | "61_90" | "over_90";
export type LateFeeType = "fixed" | "percentage";
export type InvoiceKind = "one_off" | "instalment";
export type HtmlLang = "en" | "ms";

export interface LateFeeConfig {
  lateFeeType: LateFeeType | null;
  lateFeeAmount?: number | null;
  lateFeePercentage?: number | null;
  maxLateFeeAmount?: number | null;
  gracePeriodDays?: number | null;
  autoApply?: boolean | null;
}

export interface DirectoryFilter {
  query?: string;
  state?: string;
  ageGroup?: string;
  feeMin?: number | null;
  feeMax?: number | null;
  trainingDay?: string;
  famOnly?: boolean;
}

export interface DirectoryClubCard {
  slug: string;
  name: string;
  location: string;
  state: string;
  ageGroups: string[];
  feeFrom: number;
  feeTo: number;
  trainingDays: string[];
  famAccredited: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function utcDay(value: Date): number {
  return Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
}

export function dayDiff(due: Date, today: Date): number {
  return Math.round((utcDay(today) - utcDay(due)) / DAY_MS);
}

export function calculateLateFee(principal: number, config: LateFeeConfig): number {
  if (!config.autoApply) return 0;
  const base = Math.max(0, Number(principal) || 0);
  let fee = 0;
  if (config.lateFeeType === "fixed") fee = Math.max(0, Number(config.lateFeeAmount) || 0);
  if (config.lateFeeType === "percentage") {
    fee = (base * Math.max(0, Number(config.lateFeePercentage) || 0)) / 100;
  }
  const cap = config.maxLateFeeAmount;
  if (cap != null && Number(cap) > 0 && fee > Number(cap)) fee = Number(cap);
  return Math.round(fee * 100) / 100;
}

export function lateFeeIsDue(due: Date, today: Date, gracePeriodDays: number | null | undefined): boolean {
  const grace = Math.max(0, gracePeriodDays ?? 0);
  return dayDiff(due, today) > grace;
}

export interface LateFeeWindow {
  enabledAt: Date | null;
  applyToExistingOverdue?: boolean | null;
  gracePeriodDays?: number | null;
}

/** Calendar day a bill first counts as overdue: due date, plus grace days, plus one. */
export function lateFeeOverdueOn(due: Date, gracePeriodDays: number | null | undefined): Date {
  const grace = Math.max(0, gracePeriodDays ?? 0);
  return new Date(Date.UTC(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate() + grace + 1));
}

/**
 * Late fees apply only to bills that become overdue after the club turns them on.
 * Existing overdue bills stay untouched unless the club sets applyToExistingOverdue.
 * The fee amount itself always comes from the club config at apply time.
 */
export function lateFeeAppliesToBill(due: Date, config: LateFeeWindow): boolean {
  if (config.applyToExistingOverdue) return true;
  if (!config.enabledAt) return false;
  const overdueOn = lateFeeOverdueOn(due, config.gracePeriodDays);
  const enabledDay = Date.UTC(
    config.enabledAt.getUTCFullYear(),
    config.enabledAt.getUTCMonth(),
    config.enabledAt.getUTCDate(),
  );
  return overdueOn.getTime() > enabledDay;
}

export function ageingBucket(due: Date, today: Date): AgeingBucket {
  const diff = dayDiff(due, today);
  if (diff <= 0) return "current";
  if (diff <= 30) return "1_30";
  if (diff <= 60) return "31_60";
  if (diff <= 90) return "61_90";
  return "over_90";
}

export function sumAgeing(
  rows: Array<{ due: Date; outstanding: number }>,
  today: Date,
): Record<AgeingBucket, number> {
  const buckets: Record<AgeingBucket, number> = {
    current: 0,
    "1_30": 0,
    "31_60": 0,
    "61_90": 0,
    over_90: 0,
  };
  for (const row of rows) {
    const amount = Math.max(0, Number(row.outstanding) || 0);
    buckets[ageingBucket(row.due, today)] += amount;
  }
  return buckets;
}

export function retentionRate(started: number, stillActive: number): number {
  if (started <= 0) return 0;
  const kept = Math.min(Math.max(0, stillActive), started);
  return Math.round((kept / started) * 1000) / 10;
}

export function revenueByProgramme(
  rows: Array<{ programme: string; collected: number; outstanding: number }>,
): Array<{ programme: string; collected: number; outstanding: number }> {
  const map = new Map<string, { collected: number; outstanding: number }>();
  for (const row of rows) {
    const current = map.get(row.programme) ?? { collected: 0, outstanding: 0 };
    current.collected += Number(row.collected) || 0;
    current.outstanding += Number(row.outstanding) || 0;
    map.set(row.programme, current);
  }
  return [...map.entries()].map(([programme, totals]) => ({ programme, ...totals }));
}

export function rsvpIsLocked(lockAt: Date | null | undefined, now: Date): boolean {
  if (!lockAt) return false;
  return lockAt.getTime() <= now.getTime();
}

export function reminderWindow(eventAt: Date, now: Date, hoursBefore = 48): boolean {
  const delta = eventAt.getTime() - now.getTime();
  const windowMs = hoursBefore * 60 * 60 * 1000;
  return delta > 0 && delta <= windowMs;
}

export function siblingDiscount(baseFee: number, siblingCount: number, percent: number): number {
  if (siblingCount < 2 || percent <= 0) return Math.max(0, baseFee);
  const discounted = baseFee * (1 - Math.min(percent, 100) / 100);
  return Math.round(Math.max(0, discounted) * 100) / 100;
}

export function instalmentAmounts(total: number, count: number): number[] {
  const safeCount = Math.max(1, Math.floor(count));
  const cents = Math.round(Math.max(0, total) * 100);
  const each = Math.floor(cents / safeCount);
  const remainder = cents - each * safeCount;
  return Array.from({ length: safeCount }, (_, index) => (each + (index === 0 ? remainder : 0)) / 100);
}

export function instalmentDates(start: Date, count: number): Date[] {
  return Array.from({ length: Math.max(1, count) }, (_, index) => {
    const date = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + index, start.getUTCDate()));
    return date;
  });
}

export function waitlistCanOffer(capacity: number, enrolled: number): boolean {
  if (capacity <= 0) return false;
  return enrolled < capacity;
}

export interface ComplianceCheck {
  kind: "licence" | "jkm" | "first_aid" | "safeguarding";
  status: "valid" | "expired" | "missing" | "pending";
  expiresOn?: Date | null;
}

export function staffBlockedForMinors(checks: ComplianceCheck[], today: Date): boolean {
  const required: ComplianceCheck["kind"][] = ["licence", "jkm", "safeguarding"];
  return required.some((kind) => {
    const check = checks.find((item) => item.kind === kind);
    if (!check || check.status !== "valid") return true;
    if (check.expiresOn && check.expiresOn.getTime() < utcDay(today)) return true;
    return false;
  });
}

export function sessionHeadingAllowed(ageMin: number | null, ageMax: number | null, includesHeading: boolean): boolean {
  if (!includesHeading) return true;
  return headingGuidanceForAges(ageMin, ageMax).allowsHeading;
}

export interface PriceRule {
  kind: "peak" | "off_peak" | "weekend" | "special" | "member" | "club";
  price: number;
  dayOfWeek?: number | null;
  specialDate?: string | null;
}

export function priceForSlot(base: number, when: Date, rules: PriceRule[], audience: "public" | "member" | "club" = "public"): number {
  const date = when.toISOString().slice(0, 10);
  const special = rules.find((rule) => rule.kind === "special" && rule.specialDate === date);
  if (special) return special.price;
  if (audience === "club") {
    const club = rules.find((rule) => rule.kind === "club");
    if (club) return club.price;
  }
  if (audience === "member") {
    const member = rules.find((rule) => rule.kind === "member");
    if (member) return member.price;
  }
  const weekend = when.getUTCDay() === 0 || when.getUTCDay() === 6;
  if (weekend) {
    const rule = rules.find((item) => item.kind === "weekend");
    if (rule) return rule.price;
  }
  const hour = when.getUTCHours();
  const peak = hour >= 18 && hour < 23;
  const timed = rules.find((item) => item.kind === (peak ? "peak" : "off_peak"));
  return timed?.price ?? base;
}

export function cancellationRefund(input: {
  paid: number;
  hoursBeforeStart: number;
  cancellationWindowHours: number;
  refundPercent: number;
}): number {
  if (input.hoursBeforeStart < input.cancellationWindowHours) return 0;
  const percent = Math.min(100, Math.max(0, input.refundPercent));
  return Math.round(input.paid * percent) / 100;
}

export function splitShares(total: number, people: number): number[] {
  return instalmentAmounts(total, Math.max(1, people));
}

export function openMatchStatus(joined: number, capacity: number, deadlinePassed: boolean): "open" | "filled" | "refund_pending" {
  if (joined >= capacity && capacity > 0) return "filled";
  if (deadlinePassed) return "refund_pending";
  return "open";
}

export function htmlLang(language: string | null | undefined): HtmlLang {
  return (language ?? "").toLowerCase().startsWith("ms") ? "ms" : "en";
}

export function filterDirectory(clubs: DirectoryClubCard[], filter: DirectoryFilter): DirectoryClubCard[] {
  const query = filter.query?.trim().toLowerCase() ?? "";
  return clubs.filter((club) => {
    if (query && !`${club.name} ${club.location} ${club.state}`.toLowerCase().includes(query)) return false;
    if (filter.state && filter.state !== "all" && club.state !== filter.state) return false;
    if (filter.ageGroup && filter.ageGroup !== "all" && !club.ageGroups.includes(filter.ageGroup)) return false;
    if (filter.feeMin != null && club.feeTo < filter.feeMin) return false;
    if (filter.feeMax != null && club.feeFrom > filter.feeMax) return false;
    if (filter.trainingDay && filter.trainingDay !== "all" && !club.trainingDays.includes(filter.trainingDay)) return false;
    if (filter.famOnly && !club.famAccredited) return false;
    return true;
  });
}

export interface SitemapEntry {
  path: string;
  changefreq?: "daily" | "weekly" | "monthly";
}

export function sitemapXml(origin: string, entries: SitemapEntry[]): string {
  const body = entries
    .map((entry) => {
      const loc = `${origin.replace(/\/$/, "")}${entry.path.startsWith("/") ? entry.path : `/${entry.path}`}`;
      return `  <url><loc>${loc}</loc><changefreq>${entry.changefreq ?? "weekly"}</changefreq></url>`;
    })
    .join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${body}\n</urlset>\n`;
}

export function sportsEventJsonLd(input: {
  name: string;
  startDate: string;
  locationName: string;
  url: string;
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    name: input.name,
    startDate: input.startDate,
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: "https://schema.org/EventScheduled",
    location: {
      "@type": "Place",
      name: input.locationName,
      address: { "@type": "PostalAddress", addressCountry: "MY" },
    },
    url: input.url,
  };
}

export const NOTIFICATION_CATEGORIES = [
  "fees",
  "sessions",
  "matches",
  "welfare",
  "bookings",
  "alerts",
] as const;

export const NOTIFICATION_CHANNELS = ["in_app", "email", "push", "whatsapp"] as const;

export function defaultNotificationPrefs(): Array<{ category: string; channel: string; enabled: boolean }> {
  return NOTIFICATION_CATEGORIES.flatMap((category) =>
    NOTIFICATION_CHANNELS.map((channel) => ({
      category,
      channel,
      enabled: channel === "in_app" || channel === "email" || (category === "alerts" && channel === "push"),
    })),
  );
}

export function aiUsageAllowed(used: number, limit: number): boolean {
  if (limit <= 0) return false;
  return used < limit;
}

export function minorPromptAllowed(prompt: string): boolean {
  const text = prompt.toLowerCase();
  const blocked = ["home address", "phone number", "medical record", "national id", "mykad"];
  return !blocked.some((phrase) => text.includes(phrase));
}

export function calendarFeedPath(token: string): string {
  if (!/^[a-zA-Z0-9_-]{16,}$/.test(token)) throw new Error("Feed token is too short");
  return `/functions/v1/calendar-feed?token=${encodeURIComponent(token)}`;
}

export function rotateToken(bytes: Uint8Array): string {
  return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
}

export function receiptLines(input: {
  clubName: string;
  title: string;
  amount: number;
  currency: string;
  method: string;
  reference: string;
}): string[] {
  return [
    input.clubName,
    input.title,
    `${input.currency} ${input.amount.toFixed(2)}`,
    input.method,
    input.reference,
    "Pay at club. This is not a tax invoice until the club issues one.",
  ];
}

export function platformPrice(input: {
  activePlayers: number;
  perPlayer: number;
  venuePlan: number;
  organizerFee: number;
  transactionBps: number;
  volume: number;
}): number {
  const players = Math.max(0, input.activePlayers) * input.perPlayer;
  const tx = (Math.max(0, input.volume) * Math.max(0, input.transactionBps)) / 10000;
  return Math.round((players + input.venuePlan + input.organizerFee + tx) * 100) / 100;
}

export function supporterShare(amount: number, clubPercent: number): { club: number; platform: number } {
  const percent = Math.min(100, Math.max(0, clubPercent));
  const club = Math.round(amount * percent) / 100;
  return { club, platform: Math.round((amount - club) * 100) / 100 };
}

export function imageVariant(src: string, width: number): { src: string; type: string } {
  const clean = src.split("?")[0] ?? src;
  if (/\.avif$/i.test(clean)) return { src: clean, type: "image/avif" };
  if (/\.webp$/i.test(clean)) return { src: clean, type: "image/webp" };
  return { src: `${clean}?w=${width}`, type: "image/jpeg" };
}
